import { Controller } from "@hotwired/stimulus"

/**
 * Comments Panel Controller
 * 
 * Handles opening and closing the comments panel and loading comments.
 */
export default class extends Controller {
  static targets = ["panel", "overlay", "closeButton", "frame", "loader"]
  
  static values = {
    commentableId: String,
    commentableType: String
  }

  connect() {
    // Set up event listeners
    this.setupEventListeners();
    
    // Register global handler for backward compatibility
    window.openCommentsPanel = this.openCommentsPanelGlobal.bind(this);
  }

  /**
   * Set up event listeners for the comments panel
   */
  setupEventListeners() {
    if (this.hasCloseButtonTarget) {
      this.closeButtonTarget.addEventListener('click', this.close.bind(this));
    }
    
    if (this.hasOverlayTarget) {
      this.overlayTarget.addEventListener('click', this.close.bind(this));
    }
  }

  /**
   * Open the comments panel and load comments
   * @param {Event} event - The event that triggered the open action
   */
  open(event) {
    if (event) event.preventDefault();
    
    // Get commentable info from the clicked element if available
    const clickedElement = event.currentTarget;
    const commentableId = clickedElement.dataset.commentableId || this.commentableIdValue;
    const commentableType = clickedElement.dataset.commentableType || this.commentableTypeValue;
    
    this.openWithParams(commentableId, commentableType);
  }
  
  /**
   * Open comments panel with specific parameters
   * @param {string} commentableId - The ID of the commentable object
   * @param {string} commentableType - The type of the commentable object
   */
  openWithParams(commentableId, commentableType) {
    if (!commentableId || !commentableType) {
      console.error("Missing commentableId or commentableType", { commentableId, commentableType });
      return;
    }
    
    console.log("Opening comments panel for", commentableType, commentableId);
    
    // Load comments content via Turbo frame if we have a frame target
    if (this.hasFrameTarget && commentableId && commentableType) {
      let src = '';
      
      if (commentableType === 'AcademyPost') {
        src = `/academy_posts/${commentableId}/comments/modal`;
      } else if (commentableType === 'Post') {
        src = `/posts/${commentableId}/comments/modal`;
      }
      
      if (src) {
        console.log("Setting frame src to", src);
        this.frameTarget.src = src;
      }
    }
    
    // Try to find and configure comment loader if available
    this.configureCommentLoader(commentableId, commentableType);
    
    // Add class to body to show the panel
    document.body.classList.add('comment-section-open');
  }
  
  /**
   * Configure comment loader with commentable info
   * @param {string} commentableId - The ID of the commentable object
   * @param {string} commentableType - The type of the commentable object
   */
  configureCommentLoader(commentableId, commentableType) {
    console.log("Configuring comment loader with", commentableType, commentableId);
    
    // Find all comment loaders within our element first
    const loaders = this.element.querySelectorAll('[data-controller="comment-loader"]');
    if (loaders.length > 0) {
      console.log("Found", loaders.length, "comment loaders within panel");
      
      // Configure each loader
      loaders.forEach(loader => {
        this.configureLoader(loader, commentableId, commentableType);
      });
      
      return;
    }
    
    // Otherwise, look for any comment-loader in the document
    const commentLoader = document.querySelector('[data-controller="comment-loader"]');
    if (commentLoader) {
      console.log("Found comment loader in document");
      this.configureLoader(commentLoader, commentableId, commentableType);
    } else {
      console.warn("No comment loader found");
    }
  }
  
  configureLoader(loader, commentableId, commentableType) {
    // Set data attributes
    loader.setAttribute('data-comment-loader-commentable-id-value', commentableId);
    loader.setAttribute('data-comment-loader-commentable-type-value', commentableType);
    
    // Try to get the controller
    try {
      // For Stimulus 2.0+
      if (window.Stimulus?.getControllerForElementAndIdentifier) {
        const controller = window.Stimulus.getControllerForElementAndIdentifier(
          loader, 'comment-loader'
        );
        
        if (controller && typeof controller.loadComments === 'function') {
          console.log("Calling loadComments on controller via Stimulus");
          controller.loadComments();
          return;
        }
      }
      
      // Alternative approach for Stimulus 3.0+
      const application = window.Stimulus?.application;
      if (application) {
        const controllers = application.controllers;
        const controller = controllers.find(c => 
          c.element === loader && c.identifier === 'comment-loader'
        );
        
        if (controller && typeof controller.loadComments === 'function') {
          console.log("Calling loadComments on controller via application.controllers");
          controller.loadComments();
          return;
        }
      }
      
      // Direct dispatch as last resort
      console.log("Dispatching loadComments event directly");
      const event = new Event('loadComments');
      loader.dispatchEvent(event);
      
    } catch (error) {
      console.error("Error configuring comment loader:", error);
    }
  }

  /**
   * Global function for opening comments panel (for backward compatibility)
   * @param {string} commentableId - The ID of the commentable object
   * @param {string} commentableType - The type of the commentable object
   */
  openCommentsPanelGlobal(commentableId, commentableType) {
    this.openWithParams(commentableId, commentableType);
  }

  /**
   * Close the comments panel
   */
  close() {
    document.body.classList.remove('comment-section-open');
  }
}
